import { Container, Sprite, type DestroyOptions, type Texture } from "pixi.js";
import type { Log, PresentationTaskRunner } from "@slot/presentation";
import { POTS, type PotKind, type PotLevels } from "../rules/symbols";
import { POTION_POTS_TUNING } from "../tuning/featureTuning";
import { ART_SCALE, POTION_POTS_LAYOUT, POTS_LAYOUT, art } from "./layout";
import { FX_SCOPE } from "./fxScope";
import { PotShine } from "./PotShine";
import { PotSmoke } from "./PotSmoke";

/** Each pot's colours: its name's fill and outline where the game writes it (the trigger panel), its glow's tint. */
export const POT_COLOURS: Readonly<Record<PotKind, { fill: number; stroke: number; glow: number }>> = {
  life: { fill: 0x5cff4a, stroke: 0x0b3d05, glow: 0x46ff70 },
  multi: { fill: 0xff4a3a, stroke: 0x4a0404, glow: 0xff5a3a },
  grow: { fill: 0x5ab0ff, stroke: 0x061f5a, glow: 0x4aa0ff },
};

/**
 * The artist's Rive pots (`potion_pots.riv`, played as its Spine conversion): its view model's numbers, the pots'
 * potion levels — 0 the empty urn, 1 to 3 the gems, 4 overflowing; a change shows at once (the moment's impact covers
 * it).
 */
export const POTION_POTS_RIVE = {
  levels: { life: "lifeLevel", multi: "multiLevel", grow: "growLevel" } satisfies Record<PotKind, string>,
  maxLevel: 4,
} as const;

/** What the pots ask of their art (`SpineArtboard`: the Rive file's conversion, drawn into a texture of its own). */
export interface PotionArt {
  region(x: number, y: number, width: number, height: number): Texture;
  resize(scale: number): void;
  setNumber(name: string, value: number): void;
  advance(deltaSeconds: number): boolean;
  render(deltaSeconds: number): void;
  dispose(): void;
}

/** How the glow behind a pot reads: none while empty, soft while charged, full while its booster is in play. */
const GLOW = { size: 1.45, charged: 0.45, lit: 0.95, spent: 0 } as const;

/** The pile art's mouth line, as a fraction of its height (tools/art: `potPile`). */
const PILE_LIP = 94 / 110;

type BoosterState = "lit" | "spent" | "off";

interface Pot {
  /** At the pot's spot; `body` scales inside it (a bump, an eruption). */
  root: Container;
  body: Container;
  glow: Sprite | null;
  urn: Sprite | null;
  pile: Sprite;
  /** Its part of the Rive pots, once they are loaded, over the urn. */
  potion: Sprite | null;
  /** The pot's drawn size, in design units. */
  width: number;
  height: number;
}

/**
 * The three pots over the grid, as the mock-up stacks them: each urn with a glow behind it (soft while charged, full
 * while its booster plays) and a coin pile in its mouth that grows with its gems (1–3), then a heap of coins in front of
 * their bases whose lower half the grid hides. Their names go in front of the grid as well: `names` is a layer of its
 * own, which the scene places over the grid; so is `smoke`, a booster's plume out of its pot while it plays, which the
 * scene places over the pots and under the plaques and the logo, and `shine`, the artist's shine always playing behind
 * the pots, which the scene places under them. Once the artist's Rive pots have loaded
 * (`setLiveArt`), each pot shows its part of them over its urn — the urn with its potion, whose level is its gems, 4
 * while its booster plays, the urn empty at 0 — and no glow or pile. The urns stay under the parts. Motion runs on the runner it is given (the
 * presentation's: a slam or a speed mode scales it with the round); the Rive pots play on the UI's (`ambientRunner`)
 * while a pot holds potion, and for a moment after a change (an empty urn is still), never sped up, never work a round
 * waits for.
 */
export class PotsView extends Container {
  /** The pots' names (the scene adds this layer over the grid). */
  public readonly names: Container;
  /** The plumes of the boosters in play (the scene adds this layer over the pots, under the plaques and the logo). */
  public readonly smoke: PotSmoke;
  /** The shine behind the pots, always playing (the scene adds this layer under the pots). */
  public readonly shine: PotShine;
  private readonly pots: Record<PotKind, Pot>;
  private readonly piles: readonly (Texture | null)[];
  private readonly runner: PresentationTaskRunner;
  private readonly ambientRunner: PresentationTaskRunner;
  private readonly log: Log;
  private readonly gems: Record<PotKind, number> = { life: 0, multi: 0, grow: 0 };
  private readonly boosters: Record<PotKind, BoosterState> = { life: "off", multi: "off", grow: "off" };
  /** The levels the Rive pots were last given (-1: none yet). */
  private readonly levels: Record<PotKind, number> = { life: -1, multi: -1, grow: -1 };
  private art: PotionArt | null = null;
  /** The Rive pots are drawn frame by frame (a pot holds potion, or a change settles). */
  private drawing = false;
  /** Engine seconds the Rive pots are still drawn after a change though every pot is empty. */
  private settling = 0;
  /** Device pixels per design unit where the pots are drawn (their art's texture size follows it). */
  private pixelsPerUnit = 1;

  public constructor(input: {
    pots: Readonly<Record<PotKind, Texture | null>>;
    labels: Readonly<Record<PotKind, Texture | null>>;
    heap: Texture | null;
    piles: readonly (Texture | null)[];
    glow: Texture | null;
    runner: PresentationTaskRunner;
    /** The UI's runner: the Rive pots play on it. */
    ambientRunner: PresentationTaskRunner;
    log: Log;
  }) {
    super({ label: "Pots" });
    this.piles = input.piles;
    this.runner = input.runner;
    this.ambientRunner = input.ambientRunner;
    this.log = input.log;
    const urns = new Container({ label: "Urns" });
    const names = new Container({ label: "PotNames" });
    this.names = names;
    this.smoke = new PotSmoke({ ambientRunner: input.ambientRunner, log: input.log });
    this.shine = new PotShine({ ambientRunner: input.ambientRunner, log: input.log });
    const build = (kind: PotKind): Pot => {
      const spec = POTS_LAYOUT[kind];
      const width = art(spec.art.width);
      const height = art(spec.art.height);
      const root = new Container({ label: `Pot:${kind}` });
      root.position.set(spec.x, spec.y);
      const body = new Container({ label: "Body" });
      let glow: Sprite | null = null;
      if (input.glow) {
        glow = new Sprite({ texture: input.glow, anchor: 0.5, label: "Glow", tint: POT_COLOURS[kind].glow });
        glow.setSize(width * GLOW.size, width * GLOW.size);
        glow.alpha = 0;
        body.addChild(glow);
      }
      const urnArt = input.pots[kind];
      let urn: Sprite | null = null;
      if (urnArt) {
        urn = new Sprite({ texture: urnArt, anchor: 0.5, label: "Urn" });
        urn.setSize(width, height);
        body.addChild(urn);
      }
      const pile = new Sprite({ anchor: { x: 0.5, y: PILE_LIP }, label: "Pile" });
      pile.position.set(0, POTS_LAYOUT.mouth.y * height);
      pile.visible = false;
      body.addChild(pile);
      root.addChild(body);
      urns.addChild(root);
      const nameArt = input.labels[kind];
      if (nameArt) {
        const name = new Sprite({ texture: nameArt, anchor: 0.5, label: `Name:${kind}` });
        name.setSize(art(spec.label.width) * spec.label.scale, art(spec.label.height) * spec.label.scale);
        name.position.set(spec.x + spec.label.x, spec.y + spec.label.y);
        names.addChild(name);
      }
      return { root, body, glow, urn, pile, potion: null, width, height };
    };
    this.pots = { life: build("life"), multi: build("multi"), grow: build("grow") };
    this.addChild(urns);
    if (input.heap) {
      const { heap } = POTS_LAYOUT;
      const coins = new Sprite({ texture: input.heap, anchor: 0.5, label: "CoinHeap" });
      coins.setSize(heap.art.width * heap.scale, heap.art.height * heap.scale);
      coins.position.set(heap.x, heap.y);
      this.addChild(coins);
    }
  }

  /**
   * The artist's Rive pots, loaded: each pot's part on a sprite of its own over its urn; the glows and the coin piles go
   * (the potion is the file's), the urns stay under the parts.
   */
  public setLiveArt(potion: PotionArt): void {
    if (this.destroyed || this.art) {
      potion.dispose();
      return;
    }
    this.art = potion;
    potion.resize(this.canvasScale());
    const { topLeft, parts, artboard } = POTION_POTS_LAYOUT;
    for (const kind of POTS) {
      const pot = this.pots[kind];
      const [from, to] = parts[kind];
      const sprite = new Sprite({ texture: potion.region(from, 0, to - from, artboard.height), label: "Potion" });
      sprite.setSize(art(to - from), art(artboard.height));
      sprite.position.set(topLeft.x + art(from) - POTS_LAYOUT[kind].x, topLeft.y - POTS_LAYOUT[kind].y);
      pot.body.addChildAt(sprite, pot.urn ? pot.body.getChildIndex(pot.urn) + 1 : 0);
      pot.potion = sprite;
      if (pot.glow) pot.glow.visible = false;
      pot.pile.visible = false;
      pot.body.alpha = 1;
    }
    for (const kind of POTS) this.applyLevel(kind);
    this.redraw(potion);
    this.log("[Pots] the Rive pots are in");
  }

  /** Device pixels per design unit where the pots are drawn: their art's texture is drawn at their size on the screen. */
  public setPixelsPerUnit(pixelsPerUnit: number): void {
    if (!(pixelsPerUnit > 0) || pixelsPerUnit === this.pixelsPerUnit) return;
    this.pixelsPerUnit = pixelsPerUnit;
    this.art?.resize(this.canvasScale());
    this.smoke.setPixelsPerUnit(pixelsPerUnit);
    this.shine.setPixelsPerUnit(pixelsPerUnit);
  }

  /** The Rive pots (the dev hook's read): whether they are in, and each pot's potion level (0: the empty urn). */
  public get potion(): { loaded: boolean } & Record<PotKind, number> {
    return { loaded: this.art !== null, life: this.potionLevel("life"), multi: this.potionLevel("multi"), grow: this.potionLevel("grow") };
  }

  /** Every pot's pile and soft glow for its gems (the base game's state). */
  public setLevels(levels: PotLevels): void {
    for (const kind of POTS) this.setLevel(kind, levels[kind]);
  }

  /** One pot's pile and glow for `level` gems (the Rive pots: its potion's level). */
  public setLevel(kind: PotKind, level: number): void {
    const pot = this.pots[kind];
    this.gems[kind] = level;
    const texture = level > 0 ? (this.piles[Math.min(level, this.piles.length) - 1] ?? null) : null;
    pot.pile.visible = texture !== null && this.art === null;
    if (texture) {
      const width = pot.width * POTS_LAYOUT.mouth.width;
      pot.pile.texture = texture;
      pot.pile.setSize(width, (width * texture.height) / Math.max(1, texture.width));
    }
    if (pot.glow) pot.glow.alpha = this.glowAlpha(kind);
    this.applyLevel(kind);
  }

  /**
   * A booster in play glows full (its Rive pot overflows) and plumes; a spent one (LIFE's refill used) goes dark (its
   * gems'), its plume dissipating.
   */
  public setBooster(kind: PotKind, state: BoosterState): void {
    this.boosters[kind] = state;
    this.smoke.set(kind, state === "lit");
    const glow = this.pots[kind].glow;
    if (glow) glow.alpha = this.glowAlpha(kind);
    this.pots[kind].body.alpha = state === "spent" && this.art === null ? 0.55 : 1;
    this.applyLevel(kind);
  }

  /** A pot's node, its urn, glow and pile (the shine glints across it). */
  public node(kind: PotKind): Container {
    return this.pots[kind].root;
  }

  /** A pot's mouth's rim: its middle and half its width, in the composition's space (where its sparkles twinkle). */
  public rim(kind: PotKind): { x: number; y: number; halfWidth: number } {
    return { ...this.mouth(kind), halfWidth: (this.pots[kind].width * POTS_LAYOUT.mouth.width) / 2 };
  }

  /** Where a gem drops into the pot and its impact plays: its opening (the artist's), in the composition's space. */
  public mouth(kind: PotKind): { x: number; y: number } {
    return { ...POTS_LAYOUT.opening[kind] };
  }

  /** A gem landing: the pot squashes and springs back. */
  public async bump(kind: PotKind, seconds: number): Promise<void> {
    const pot = this.pots[kind];
    await this.runner.tweenProgress(seconds, (k) => {
      if (pot.body.destroyed) return;
      pot.body.scale.set(1 + 0.06 * Math.sin(Math.PI * k), 1 + 0.14 * Math.sin(Math.PI * k));
    }, FX_SCOPE, { easing: "linear" });
  }

  /** The pot erupting: it shakes, swells and flashes its glow; `atPeak` runs at its height (a change goes under it). */
  public async erupt(kind: PotKind, seconds: number, atPeak?: () => void): Promise<void> {
    const pot = this.pots[kind];
    const baseX = pot.root.x;
    let peaked = false;
    const peak = (): void => {
      if (peaked) return;
      peaked = true;
      atPeak?.();
    };
    await this.runner.tweenProgress(seconds, (k) => {
      if (pot.body.destroyed) return;
      if (k >= 0.5) peak();
      const swell = 1 + 0.18 * Math.sin(Math.PI * k);
      pot.body.scale.set(swell, swell);
      pot.root.x = baseX + 10 * Math.sin(k * Math.PI * 10) * (1 - k);
      if (pot.glow) pot.glow.alpha = Math.max(this.glowAlpha(kind), Math.sin(Math.PI * k));
    }, FX_SCOPE, { easing: "linear" });
    peak();
    pot.root.x = baseX;
    if (pot.glow) pot.glow.alpha = this.glowAlpha(kind);
  }

  public override destroy(options?: DestroyOptions): void {
    this.art?.dispose();
    this.art = null;
    super.destroy(options);
  }

  /** The glow a pot rests at: its booster's, else its gems'. */
  private glowAlpha(kind: PotKind): number {
    const state = this.boosters[kind];
    if (state === "lit") return GLOW.lit;
    if (state === "spent") return GLOW.spent;
    return this.gems[kind] > 0 ? GLOW.charged : 0;
  }

  /** A pot's potion level: its gems; a booster in play overflows. */
  private potionLevel(kind: PotKind): number {
    return this.boosters[kind] === "lit" ? POTION_POTS_RIVE.maxLevel : Math.min(POTION_POTS_RIVE.maxLevel - 1, this.gems[kind]);
  }

  /** The Rive pots at a pot's level (a change only). */
  private applyLevel(kind: PotKind): void {
    const art = this.art;
    if (!art) return;
    const level = this.potionLevel(kind);
    if (this.levels[kind] === level) return;
    this.levels[kind] = level;
    art.setNumber(POTION_POTS_RIVE.levels[kind], level);
    this.redraw(art);
  }

  /** Canvas pixels per artboard pixel: the artboard's pixels are the delivery's, drawn at the art's scale. */
  private canvasScale(): number {
    return ART_SCALE * this.pixelsPerUnit;
  }

  /** Whether any pot holds potion (its level animates; an empty urn is still). */
  private get anyPotion(): boolean {
    return POTS.some((kind) => this.potionLevel(kind) > 0);
  }

  /**
   * The Rive pots drawn as they are now, then on the UI's runner at their own pace while a pot holds potion, or for a
   * moment after the change (`settleSeconds`: an empty urn is still, but the file's images may yet be decoding).
   */
  private redraw(art: PotionArt): void {
    art.render(0);
    this.settling = POTION_POTS_TUNING.settleSeconds;
    if (this.drawing) return;
    this.drawing = true;
    let last = 0;
    void this.ambientRunner
      .tickInScope(
        "",
        (elapsed) => {
          art.advance(elapsed - last);
          this.settling = Math.max(0, this.settling - (elapsed - last));
          last = elapsed;
        },
        () => this.destroyed || this.art !== art || (!this.anyPotion && this.settling === 0),
      )
      .then(() => {
        this.drawing = false;
        // A change came in as the last settled.
        if (!this.destroyed && this.art === art && (this.anyPotion || this.settling > 0)) this.redraw(art);
      });
  }
}
